/**
 * 날짜 매크로 — 「DAG 작성 가이드」가 경고하는 조용한 실패를 식으로 고정한다.
 *
 * ## 왜 사용자가 쓰지 않고 여기서 만드는가
 *
 * `data_interval_start`·`data_interval_end` 는 **UTC instant** 다. 가이드가 ❌ 로
 * 표시한 세 가지가 전부 "에러도 알림도 없이 조용히 틀리는" 부류다.
 *
 * 1. `.in_timezone("Asia/Seoul")` 을 빼면 09:00 KST 이전에 도는 DAG 에서
 *    **하루 이른** 날짜가 렌더된다.
 * 2. `{{ }}` 안에서 변환한 뒤 SQL 에서 또 `'Asia/Seoul'` 을 붙이면 +9 가 두 번
 *    적용돼 **18 시간** 어긋난다.
 * 3. `data_interval_start` 를 «어제» 로 생각하는 것은 **일배치일 때만** 맞다.
 *    주배치는 7 일 전, 월배치는 전월 1 일이다. 틀리면 "실행은 성공인데 0 건" 이
 *    된다.
 *
 * 세 가지 모두 `schedule` 을 알아야 올바른 식이 정해진다. 그래서 주기를 아는
 * 쪽(= 폼)이 식을 만들고, 사용자가 쓰는 것은 SQL 본문뿐이다.
 */

/** cron 으로부터 판정한 실행 주기. 날짜 식이 이 값에 따라 달라진다. */
export type ScheduleGranularity = 'hourly' | 'daily' | 'weekly' | 'monthly';

/** 이번 실행이 처리할 구간의 시작. 일배치에서는 «어제». */
export const KST_START = '{{ data_interval_start.in_timezone("Asia/Seoul") | ds }}';
/** 이번 실행이 처리할 구간의 끝 = 실행일. */
export const KST_END = '{{ data_interval_end.in_timezone("Asia/Seoul") | ds }}';

/**
 * 주기 판정. cron 5 필드 `분 시 일 월 요일` 을 본다.
 *
 * 판정 순서가 중요하다 — `0 6 1 * *`(월) 은 «일» 필드가 지정돼 있고 «요일» 은
 * `*` 다. 월을 먼저 보지 않으면 일배치로 잘못 판정한다.
 */
export function scheduleGranularity(cron: string): ScheduleGranularity {
  const fields = cron.trim().split(/\s+/);
  if (fields.length !== 5) {
    throw new Error(`cron 5 필드가 아닙니다: ${cron}`);
  }
  const [, hour, dom, , dow] = fields;
  if (dom !== '*') return 'monthly';
  if (dow !== '*') return 'weekly';
  // 시 필드에 `*`·`/`·`,`·범위가 있으면 하루에 여러 번 돈다.
  if (hour === '*' || /[*/,-]/.test(hour)) return 'hourly';
  return 'daily';
}

/** 사람이 읽는 주기 설명. 폼 안내 문구와 PR 본문에 쓴다. */
export const GRANULARITY_LABELS: Record<ScheduleGranularity, string> = {
  hourly: '시간 단위',
  daily: '일배치',
  weekly: '주배치',
  monthly: '월배치',
};

/**
 * 「지우기」 task 의 `WHERE` 절. 「넣기」 SQL 도 **같은 범위**여야 한다 —
 * 지우는 범위가 더 넓으면 빈 날짜가 생기고, 좁으면 중복이 쌓인다.
 *
 * - 일배치 `lookback_days = 1` → `col = DATE(start)`
 * - 일배치 `lookback_days = N` → `col BETWEEN DATE_SUB(DATE(start), N-1 일) AND DATE(start)`
 * - 주·월배치 → `col >= DATE(start) AND col < DATE(end)`
 *
 * 주·월배치의 끝을 `<`(미만)으로 두는 이유: `<=` 로 쓰면 실행일 하루가 이번
 * 주기와 다음 주기에 **두 번** 집계된다.
 */
export function buildDatePredicate(opts: {
  column: string;
  granularity: ScheduleGranularity;
  lookbackDays: number;
}): string {
  const { column, granularity, lookbackDays } = opts;
  if (granularity === 'daily') {
    if (lookbackDays <= 1) {
      return `${column} = DATE('${KST_START}')`;
    }
    return (
      `${column} >= DATE_SUB(DATE('${KST_START}'), INTERVAL ${lookbackDays - 1} DAY)` +
      ` AND ${column} <= DATE('${KST_START}')`
    );
  }
  return `${column} >= DATE('${KST_START}') AND ${column} < DATE('${KST_END}')`;
}

/**
 * 「넣기」 SQL 이 참조해야 하는 매크로. 지우기와 범위를 맞췄는지 검사하는 데
 * 쓴다(`schema.ts`).
 *
 * 일배치는 `start` 하나로 하루가 정해지지만, 주·월배치는 구간이므로 `end` 도
 * 필요하다 — `end` 가 없으면 사용자가 `start` 를 «어제» 로 착각한 경우다.
 */
export function requiredIntervalTokens(
  granularity: ScheduleGranularity,
): ('start' | 'end')[] {
  return granularity === 'daily' ? ['start'] : ['start', 'end'];
}

/**
 * 사용자 SQL 의 머리말. 폼 기본값으로 채워 «형태를 그대로 복사» 를 돕는다 —
 * 가이드가 날짜 토큰에 대해 요구하는 것이 정확히 그것이다.
 *
 * `predicate` 가 없으면 `WHERE` 를 붙이지 않는다 — 「지우기」 가 테이블 전체를
 * 지우는 구성이면 전체 적재가 의도이므로 날짜로 좁히면 오히려 틀린다.
 */
export function buildInsertSqlSkeleton(opts: {
  /** `` `{{ PROJECT_ID }}.{{ DATA_SET }}.table` `` */
  targetRef: string;
  columns: string[];
  /** `buildDatePredicate` 결과. 전체 적재면 생략한다. */
  predicate?: string;
  /**
   * FROM 절의 원천 테이블 식(백틱 포함) — `buildSourceRef` 결과. 폼이 원천을 바꿀 때
   * 이 문자열을 찾아 바꾸므로 여기서 다시 감싸거나 다듬지 않는다.
   */
  sourceRef: string;
}): string {
  const { targetRef, columns, predicate, sourceRef } = opts;
  const columnList = columns.length
    ? columns.map((c, i) => `${i === 0 ? '       ' : '     , '}${c}`).join('\n')
    : '       -- ← 내 테이블의 컬럼 목록';
  return [
    `INSERT INTO ${targetRef} (`,
    columnList,
    ')',
    'SELECT',
    '       -- ← 여기부터 내 SELECT',
    `  FROM ${sourceRef} o`,
    ...(predicate ? [` WHERE ${predicate}`] : []),
    '',
  ].join('\n');
}
